import React from 'react';

import { EMPLOYEE_SESSION, EMPLOYEE_SESSION_PAGES } from './constants';
import withShipment from '../../withShipment';
import ErrorFeedbackBar from '../global/ErrorFeedbackBar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import CustomToolbar from '../global/CustomToolbar';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DownloadIcon from '@material-ui/icons/GetApp';

import { statusSelector } from '../../redux/selectors/global';
import { clearStatus } from '../../redux/actions/global';
import {
  employeesSelector,
  userInfoSelector,
} from '../../redux/selectors/auth';
import {
  listEmployeeSession,
  manualClockIn,
  updateEmployeeStatus,
  deleteEmployeeSession,
  forceClockOut,
} from '../../redux/actions/settings';
import {
  ROLE_OWNER,
  ROLE_MASTER_ADMIN,
  ROLE_MANAGER,
  ROLE_TECH_ADMIN,
  ROLE_MANAGE_EMPLOYEE_SESSIONS,
} from '../global/constants';
import {
  categorySelector,
  employeeSessionSelector,
  manageBonusSelector,
} from '../../redux/selectors/settings';
import { listCategory, listManageBonus } from '../../redux/actions/settings';
import { customersSelector } from '../../redux/selectors/customer';
import { listAllCustomers } from '../../redux/actions/customer';
import { getEmployees } from '../../redux/actions/auth';

import {
  loadSessionReports,
  clearSessionReports,
  editBonusV2,
  deleteBonusV2,
  addBonusV2,
  submitBonusDecision,
  submitPointDecision,
  editRaffleV2,
  deleteRaffleV2,
  addRaffleV2,
  submitRaffleDecision,
  editTicketOutV2,
  deleteTicketOutV2,
  editTransactionV2,
  deleteTransactionV2,
  addTransactionV2,
  editCashLeftV2,
} from '../../redux/actions/employeeSessionV2';
import {
  sessionDataSelector,
  loadingSessionReportsSelector,
} from '../../redux/selectors/employeeSessionV2';

import EmployeesScreen from './screens/EmployeesScreen';
import EmployeeSessionsScreen from './screens/EmployeeSessionsScreen';
import SessionReportsScreen from './screens/SessionReportsScreen';
import ReportDetailScreen from './screens/ReportDetailScreen';
import EmployeeSessionForm from './EmployeeSessionForm';
import PdfBody from './shared/PdfBody';
import generateSessionPdf, {
  suggestedFilename,
} from './shared/generateSessionPdf';
import Utils from '../Utils/Utils';
import { deriveAssignedDay } from './screens/EmployeeSessionsScreen';
import {
  Backdrop,
  CircularProgress,
  Typography as MuiTypography,
} from '@mui/material';

const REPORT_TITLE = {
  shift: 'Shift Report',
  bonus: 'Bonus Report',
  raffle: 'Raffle Report',
  points: 'Points Report',
  ticket: 'Ticket Out Report',
};

// Spec §2 — root container, holds the 4-screen navigation stack and threads
// the Redux mutation actions down into the report detail components.
function defaultFromDate() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  d.setHours(0, 0, 0, 0);
  return d;
}
function defaultToDate() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

// Maps each loaded session to the canonical sessionId assigned at clock-in
// time by ClockService (a 3-digit number that increments globally across
// the system, matching what the Weekly Report PDF displays).
// Returns a Map { clockId → "001" | "012" | "234" }.
function computeSessionNumbers(sessions) {
  const map = new Map();
  (sessions || []).forEach((s) => {
    if (s?.clockId && s?.sessionId) {
      map.set(s.clockId, s.sessionId);
    }
  });
  return map;
}

class EmployeeSession extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: EMPLOYEE_SESSION_PAGES.EMPLOYEES,
      selectedEmployee: null,
      selectedSession: null,
      selectedReport: null,
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      fromDate: defaultFromDate(),
      toDate: defaultToDate(),
      pdfBusy: false,
      // Session the user clicked but whose reports haven't loaded yet. We
      // stash it here, show a spinner, and navigate to SESSION_REPORTS in
      // componentDidUpdate once loadingSessionReports flips false.
      pendingSession: null,
    };
    // Ref into the offscreen PdfBody — set when the REPORT_DETAIL page is
    // active so the toolbar Download button can capture + export it.
    this.pdfRef = React.createRef();
  }

  componentDidMount() {
    const locationId = this.props.user?.selectedLocationId;
    if (locationId) {
      this.props.getEmployees({ locationId });
      this.fetchSessions();
      // Pre-load lookup data the Manager Edit Mode "Add" forms need.
      this.props.listAllCustomers({ locationId });
      this.props.listManageBonus({ locationId });
      this.props.listCategory({ locationId });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar('error', this.props.status.statusText);
    }

    // Promote the pending session to SESSION_REPORTS once the load
    // completes. We accept either outcome — on success, sessionData lands
    // and we navigate; on failure, loadingSessionReports flips false with
    // no data and we just drop the spinner so the user can retry.
    if (
      this.state.pendingSession &&
      prevProps.loadingSessionReports &&
      !this.props.loadingSessionReports
    ) {
      const pending = this.state.pendingSession;
      const data = this.props.sessionData;
      const matches =
        data &&
        data.sessionId === pending.clockId &&
        data.userId === pending.userId;
      if (matches) {
        this.setState({ pendingSession: null });
        this.setPage(EMPLOYEE_SESSION_PAGES.SESSION_REPORTS, {
          selectedSession: pending,
        });
      } else {
        // Failure path — drop the spinner; the global error bar shows why.
        this.setState({ pendingSession: null });
      }
    }
  }

  fetchSessions = () => {
    this.props.listEmployeeSession({
      fromDate: this.state.fromDate,
      toDate: this.state.toDate,
      locationId: this.props.user.selectedLocationId,
    });
  };

  handleDateChange = (key, date) => {
    // Normalise the time component the same way the legacy
    // EmployeeSessionTable did, then re-fetch so older sessions appear.
    const d = new Date(date);
    if (key === 'fromDate') d.setHours(0, 0, 0, 0);
    else d.setHours(23, 59, 59, 999);
    this.setState({ [key]: d }, this.fetchSessions);
  };

  setPage = (page, extra = {}) =>
    this.setState({ currentPage: page, ...extra });

  handleSelectEmployee = (employee) => {
    this.setPage(EMPLOYEE_SESSION_PAGES.EMPLOYEE_SESSIONS, {
      selectedEmployee: employee,
    });
  };

  handleSelectSession = (session) => {
    const { user } = this.props;
    // Stash the click target and fire the fetch; componentDidUpdate flips
    // the page once the reports land. Until then the Backdrop spinner
    // covers the sessions screen so the user can't double-click or back
    // out into half-loaded state.
    this.setState({ pendingSession: session });
    this.props.loadSessionReports({
      userId: session.userId,
      locationId: user.selectedLocationId,
      clockId: session.clockId,
    });
  };

  // Gate for the destructive session actions (delete a session + force
  // clock-out / sign-out an active one). Elevated roles always have it;
  // other roles (e.g. Reading Guy) need the Manage Employee Sessions
  // additional-access permission explicitly granted in the wizard. When
  // absent, both the delete and clock-out actions are hidden.
  canManageSessions = () => {
    const auth = this.props.user?.authority || [];
    return (
      auth.includes(ROLE_MASTER_ADMIN) ||
      auth.includes(ROLE_MANAGER) ||
      auth.includes(ROLE_OWNER) ||
      auth.includes(ROLE_TECH_ADMIN) ||
      auth.includes(ROLE_MANAGE_EMPLOYEE_SESSIONS)
    );
  };

  handleForceClockOut = (session) => {
    if (!session || !this.canManageSessions()) return;
    this.props.forceClockOut(
      {
        clockId: session.clockId,
        userId: session.userId,
        locationId: this.props.user?.selectedLocationId,
      },
      (err, message) => {
        if (err) {
          this.openFeedbackBar('error', message || 'Failed to clock out');
        } else {
          this.openFeedbackBar('success', message);
          // Refresh the list so the closed session reflects its clock-out.
          this.fetchSessions();
        }
      }
    );
  };

  handleDeleteSession = (session) => {
    if (!session || !this.canManageSessions()) return;
    this.props.deleteEmployeeSession(
      {
        clockId: session.clockId,
        // The backend re-runs listEmployeeSession(data) after deleting, so
        // pass the current date filter + location to get the refreshed list.
        fromDate: this.state.fromDate,
        toDate: this.state.toDate,
        locationId: this.props.user.selectedLocationId,
      },
      (err, message) => {
        if (err) {
          this.openFeedbackBar('error', message || 'Failed to delete session');
        } else {
          this.openFeedbackBar('success', message);
        }
      }
    );
  };

  handleSelectReport = (reportType) => {
    this.setPage(EMPLOYEE_SESSION_PAGES.REPORT_DETAIL, {
      selectedReport: reportType,
    });
  };

  handleBackToEmployees = () => {
    this.props.clearSessionReports();
    this.setPage(EMPLOYEE_SESSION_PAGES.EMPLOYEES, {
      selectedEmployee: null,
      selectedSession: null,
      selectedReport: null,
    });
  };

  handleBackToSessions = () => {
    this.setPage(EMPLOYEE_SESSION_PAGES.EMPLOYEE_SESSIONS, {
      selectedSession: null,
      selectedReport: null,
    });
  };

  handleBackToReports = () => {
    this.setPage(EMPLOYEE_SESSION_PAGES.SESSION_REPORTS, {
      selectedReport: null,
    });
  };

  // Builds the payload that every mutation action requires (the saga
  // re-fetches the session reports after each successful mutation).
  sessionContext() {
    const s = this.state.selectedSession;
    const u = this.props.user;
    return {
      sessionId: s?.clockId,
      clockId: s?.clockId,
      userId: s?.userId,
      locationId: u?.selectedLocationId,
      modifiedBy: u?.userId,
      modifiedByName: u?.displayName || u?.firstname || u?.username,
    };
  }

  // ── Bonus handlers ───────────────────────────────────────────────────
  onEditBonus = (bonus, edits) => {
    this.props.editBonusV2({
      ...this.sessionContext(),
      bonusId: bonus.bonusId,
      ...edits,
    });
  };
  onDeleteBonus = (bonus) => {
    this.props.deleteBonusV2({
      ...this.sessionContext(),
      bonusId: bonus.bonusId,
    });
  };
  onAddBonus = (entry) => {
    this.props.addBonusV2({ ...this.sessionContext(), ...entry });
  };
  onSubmitBonusDecision = (bonus, decision) => {
    this.props.submitBonusDecision({
      ...this.sessionContext(),
      bonusId: bonus.bonusId,
      decision,
    });
  };

  // ── Raffle handlers ──────────────────────────────────────────────────
  onEditRaffle = (raffle, edits) => {
    this.props.editRaffleV2({
      ...this.sessionContext(),
      raffleId: raffle.raffleId,
      ...edits,
    });
  };
  onDeleteRaffle = (raffle) => {
    this.props.deleteRaffleV2({
      ...this.sessionContext(),
      raffleId: raffle.raffleId,
    });
  };
  onAddRaffle = (entry) => {
    this.props.addRaffleV2({ ...this.sessionContext(), ...entry });
  };
  onSubmitRaffleDecision = (raffle, decision) => {
    this.props.submitRaffleDecision({
      ...this.sessionContext(),
      raffleId: raffle.raffleId,
      decision,
    });
  };

  // ── Points handler ───────────────────────────────────────────────────
  onSubmitPointDecision = (point, decision) => {
    this.props.submitPointDecision({
      ...this.sessionContext(),
      pendingId: point.pendingId,
      decision,
    });
  };

  // ── Ticket Out handlers ──────────────────────────────────────────────
  onEditTicketOut = (ticket, edits) => {
    this.props.editTicketOutV2({
      ...this.sessionContext(),
      ticketId: ticket.ticketId,
      ...edits,
    });
  };
  onDeleteTicketOut = (ticket) => {
    this.props.deleteTicketOutV2({
      ...this.sessionContext(),
      ticketId: ticket.ticketId,
    });
  };

  // ── Transaction (Bank / Expense) handlers ─────────────────────────────
  onEditTransaction = (tx, edits) => {
    this.props.editTransactionV2({
      ...this.sessionContext(),
      transactionId: tx.transactionId,
      ...edits,
    });
  };
  onDeleteTransaction = (tx) => {
    this.props.deleteTransactionV2({
      ...this.sessionContext(),
      transactionId: tx.transactionId,
    });
  };
  onAddTransaction = (entry) => {
    this.props.addTransactionV2({ ...this.sessionContext(), ...entry });
  };

  // Reconciliation cash override — flows through the same audit pipeline
  // as transaction edits so the change shows up in Manager Modifications.
  onEditCashLeft = (cashLeft) => {
    this.props.editCashLeftV2({ ...this.sessionContext(), cashLeft });
  };

  // Fired when the reviewer taps "Audit Completed" on the last item in a
  // full-screen audit. The audit dialog closes itself; we surface the
  // confirmation toast on the underlying report screen.
  onAuditComplete = (message) => {
    this.openFeedbackBar('success', message || 'Audit completed');
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };
  closeFeedbackBar = () => {
    this.setState({ feedbackBarOpen: false }, () => this.props.clearStatus());
  };

  buildSessionMeta(sessionNumbers) {
    const s = this.state.selectedSession;
    if (!s) return null;
    return {
      id: sessionNumbers.get(s.clockId) || '—',
      employee: s.name,
      clockIn: s.clockIn || s.timeIn,
      clockOut: s.clockOut || s.timeOut,
      hoursWorked: s.hoursWorked,
      assignedDay: deriveAssignedDay(s),
    };
  }

  handleDownloadPdf = async (sessionMeta) => {
    if (!this.pdfRef.current || this.state.pdfBusy || !sessionMeta) return;
    this.setState({ pdfBusy: true });
    try {
      const buffer = await generateSessionPdf({ element: this.pdfRef.current });
      if (!buffer) return;
      const locationName = this.props.user?.selectedLocationName;
      Utils.handlePdfAction(
        buffer,
        'download',
        suggestedFilename({
          type: this.state.selectedReport,
          session: sessionMeta,
          locationName,
        })
      );
    } finally {
      this.setState({ pdfBusy: false });
    }
  };

  renderHeader(sessionNumbers, sessionMeta) {
    const { currentPage, selectedEmployee, selectedReport } = this.state;
    // EmployeeSessionForm renders its own TopToolbar, so suppress our
    // outer CustomToolbar on the FORM page to avoid two stacked headers.
    if (currentPage === EMPLOYEE_SESSION_PAGES.FORM) {
      return null;
    }
    const isRoot = currentPage === EMPLOYEE_SESSION_PAGES.EMPLOYEES;
    const headerButtons = [];
    if (!isRoot) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: () => {
          if (currentPage === EMPLOYEE_SESSION_PAGES.EMPLOYEE_SESSIONS) {
            this.handleBackToEmployees();
          } else if (currentPage === EMPLOYEE_SESSION_PAGES.SESSION_REPORTS) {
            this.handleBackToSessions();
          } else if (currentPage === EMPLOYEE_SESSION_PAGES.REPORT_DETAIL) {
            this.handleBackToReports();
          } else {
            this.handleBackToEmployees();
          }
        },
        title: 'Back',
      });
    }
    if (isRoot) {
      headerButtons.push({
        icon: <AddCircleIcon />,
        handler: () => this.setPage(EMPLOYEE_SESSION_PAGES.FORM),
        title: 'Add Manual Clock Entry',
      });
    }
    if (currentPage === EMPLOYEE_SESSION_PAGES.REPORT_DETAIL && sessionMeta) {
      headerButtons.push({
        icon: <DownloadIcon />,
        handler: () => this.handleDownloadPdf(sessionMeta),
        title: 'Download PDF',
        // Intentionally not `disabled` here — the backdrop spinner already
        // signals busy state, and a disabled child trips MUI's Tooltip
        // warning since CustomToolbar wraps without a span. The handler
        // returns early if pdfBusy so double-clicks are a no-op.
      });
    }

    // Title reflects what the user is viewing in the navigation stack.
    let title = EMPLOYEE_SESSION;
    if (
      currentPage === EMPLOYEE_SESSION_PAGES.EMPLOYEE_SESSIONS &&
      selectedEmployee
    ) {
      const name = [selectedEmployee.firstname, selectedEmployee.lastname]
        .filter(Boolean)
        .join(' ')
        .trim();
      title = name ? `Sessions · ${name}` : 'Sessions';
    } else if (
      currentPage === EMPLOYEE_SESSION_PAGES.SESSION_REPORTS &&
      sessionMeta
    ) {
      title = `Reports · #${sessionMeta.id}`;
    } else if (
      currentPage === EMPLOYEE_SESSION_PAGES.REPORT_DETAIL &&
      sessionMeta
    ) {
      const label = REPORT_TITLE[selectedReport] || 'Report';
      title = `${label} · #${sessionMeta.id}`;
    }

    return (
      <CustomToolbar
        title={title}
        createTitle=""
        backTitle=""
        headerButtons={headerButtons}
      />
    );
  }

  renderBody() {
    const { currentPage, selectedEmployee, selectedSession, selectedReport } =
      this.state;
    const {
      employeeSession,
      sessionData,
      customers,
      manageBonusList,
      categories,
      employees,
    } = this.props;
    const sessionNumbers = computeSessionNumbers(employeeSession);

    switch (currentPage) {
      case EMPLOYEE_SESSION_PAGES.FORM:
        return (
          <EmployeeSessionForm
            user={this.props.user}
            handleEditingClear={() =>
              this.setPage(EMPLOYEE_SESSION_PAGES.EMPLOYEES)
            }
            manualClockIn={this.props.manualClockIn}
            editEmployeeSession={null}
            setCurrentPage={this.setPage}
            openFeedbackBar={this.openFeedbackBar}
            employees={this.props.employees}
          />
        );
      case EMPLOYEE_SESSION_PAGES.EMPLOYEE_SESSIONS:
        return (
          <EmployeeSessionsScreen
            employee={selectedEmployee}
            sessions={employeeSession}
            sessionNumbers={sessionNumbers}
            onSelectSession={this.handleSelectSession}
            canForceClockOut={this.canManageSessions()}
            onForceClockOut={this.handleForceClockOut}
            onDeleteSession={
              this.canManageSessions() ? this.handleDeleteSession : undefined
            }
          />
        );
      case EMPLOYEE_SESSION_PAGES.SESSION_REPORTS:
        return (
          <SessionReportsScreen
            session={selectedSession}
            employee={selectedEmployee}
            sessionNumber={sessionNumbers.get(selectedSession?.clockId)}
            onSelectReport={this.handleSelectReport}
          />
        );
      case EMPLOYEE_SESSION_PAGES.REPORT_DETAIL:
        return (
          <ReportDetailScreen
            type={selectedReport}
            session={selectedSession}
            employee={selectedEmployee}
            sessionData={sessionData}
            sessionNumber={sessionNumbers.get(selectedSession?.clockId)}
            customers={customers}
            manageBonusList={manageBonusList}
            categories={categories}
            employees={employees}
            locationName={this.props.user?.selectedLocationName}
            onNavigateReport={this.handleSelectReport}
            onEditBonus={this.onEditBonus}
            onDeleteBonus={this.onDeleteBonus}
            onAddBonus={this.onAddBonus}
            onSubmitBonusDecision={this.onSubmitBonusDecision}
            onEditRaffle={this.onEditRaffle}
            onDeleteRaffle={this.onDeleteRaffle}
            onAddRaffle={this.onAddRaffle}
            onSubmitRaffleDecision={this.onSubmitRaffleDecision}
            onSubmitPointDecision={this.onSubmitPointDecision}
            onEditTicketOut={this.onEditTicketOut}
            onDeleteTicketOut={this.onDeleteTicketOut}
            onEditTransaction={this.onEditTransaction}
            onDeleteTransaction={this.onDeleteTransaction}
            onAddTransaction={this.onAddTransaction}
            onEditCashLeft={this.onEditCashLeft}
            onAuditComplete={this.onAuditComplete}
          />
        );
      case EMPLOYEE_SESSION_PAGES.EMPLOYEES:
      default:
        return (
          <EmployeesScreen
            sessions={employeeSession}
            onSelectEmployee={this.handleSelectEmployee}
            fromDate={this.state.fromDate}
            toDate={this.state.toDate}
            onDateChange={this.handleDateChange}
          />
        );
    }
  }

  render() {
    const { currentPage } = this.state;
    const {
      employeeSession,
      sessionData,
      customers,
      employees,
      manageBonusList,
    } = this.props;
    const sessionNumbers = computeSessionNumbers(employeeSession);
    const sessionMeta = this.buildSessionMeta(sessionNumbers);
    const showPdf =
      currentPage === EMPLOYEE_SESSION_PAGES.REPORT_DETAIL &&
      this.state.selectedReport &&
      sessionMeta;
    return (
      <React.Fragment>
        <ErrorFeedbackBar />
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />
        {this.renderHeader(sessionNumbers, sessionMeta)}
        {this.renderBody()}
        {showPdf && (
          // Offscreen PdfBody — kept in the live DOM so layout sizes are
          // real, positioned far off the viewport so the user never sees it.
          // html2canvas walks this subtree when the toolbar download fires.
          <div
            style={{
              position: 'fixed',
              left: -10000,
              top: 0,
              width: 720,
              background: '#fff',
              padding: 24,
              fontFamily: 'sans-serif',
              pointerEvents: 'none',
            }}
          >
            <div ref={this.pdfRef}>
              <PdfBody
                type={this.state.selectedReport}
                session={sessionMeta}
                sessionData={sessionData}
                locationName={this.props.user?.selectedLocationName}
                customers={customers}
                employees={employees}
                manageBonusList={manageBonusList}
              />
            </div>
          </div>
        )}
        <Backdrop
          open={this.state.pdfBusy}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 100,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            background: 'rgba(0,0,0,0.6)',
          }}
        >
          <CircularProgress color="inherit" />
          <MuiTypography sx={{ fontSize: 14, fontWeight: 600 }}>
            Generating PDF…
          </MuiTypography>
        </Backdrop>
        <Backdrop
          open={!!this.state.pendingSession && this.props.loadingSessionReports}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 100,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            background: 'rgba(0,0,0,0.6)',
          }}
        >
          <CircularProgress color="inherit" />
          <MuiTypography sx={{ fontSize: 14, fontWeight: 600 }}>
            Loading session reports…
          </MuiTypography>
        </Backdrop>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  status: statusSelector(state),
  user: userInfoSelector(state),
  employees: employeesSelector(state),
  employeeSession: employeeSessionSelector(state),
  sessionData: sessionDataSelector(state),
  loadingSessionReports: loadingSessionReportsSelector(state),
  customers: customersSelector(state),
  manageBonusList: manageBonusSelector(state),
  categories: categorySelector(state),
});

const actionCreators = {
  clearStatus,
  listEmployeeSession,
  updateEmployeeStatus,
  getEmployees,
  manualClockIn,
  deleteEmployeeSession,
  forceClockOut,
  listAllCustomers,
  listManageBonus,
  listCategory,
  loadSessionReports,
  clearSessionReports,
  editBonusV2,
  deleteBonusV2,
  addBonusV2,
  submitBonusDecision,
  submitPointDecision,
  editRaffleV2,
  deleteRaffleV2,
  addRaffleV2,
  submitRaffleDecision,
  editTicketOutV2,
  deleteTicketOutV2,
  editTransactionV2,
  deleteTransactionV2,
  addTransactionV2,
  editCashLeftV2,
};

export default withShipment(
  { actionCreators, mapStateToProps },
  EmployeeSession
);
